import axios from 'axios'
import LegacyProjectUpdate from './ProjectUpdate.vue'
import ProjectLeaveSlackNotificationSettings from './ProjectLeaveSlackNotificationSettings.vue'
import ProjectPeriodicSlackNotificationSettings from './ProjectPeriodicSlackNotificationSettings.vue'
import { resolveRouteRecordId } from '@/common/routeRecordId'
import { getAccessContext } from '@/services/accessContext'
import { canManageProjectAdministration } from '@/utils/accessPolicy'
import { safeErrorDigest } from '../utils/safeResponseMessage'

/**
 * プロジェクト更新画面をURLのIDから復元し、Permissionに応じて既存フォームを表示する。
 *
 * <p>PROJECT_EDIT保持者には登録・更新フォームだけを表示する。削除・所属変更と同様、
 * プロジェクト通知設定はPROJECT_MANAGE保持者にだけ追加表示する。</p>
 *
 * <p>旧フォームはルート内の互換valueを参照するため、API取得後に互換データをセットしてから
 * 初めて子コンポーネントを生成する。これによりF5時にも空フォームを描画しない。</p>
 */
export default {
  name: 'ProjectUpdatePage',
  data () {
    return {
      project: null,
      projectId: null,
      accessContext: null,
      loadingProject: false,
      projectError: '',
      // 送信先チャンネルは通知先パネルが正本。保存のたびに増やし、
      // 定期コメント側へ「取り直せ」と伝える。
      channelVersion: 0
    }
  },
  computed: {
    canManageProjectAdministration () {
      return canManageProjectAdministration(this.accessContext)
    }
  },
  watch: {
    '$route.fullPath': 'loadProject'
  },
  created () {
    this.loadProject()
  },
  methods: {
    async loadProject () {
      this.projectError = ''
      this.project = null
      this.projectId = resolveRouteRecordId(this.$route)
      if (!this.projectId) {
        this.projectError = 'プロジェクトを特定できません。一覧から選択し直してください。'
        return
      }

      this.loadingProject = true
      try {
        const [context, response] = await Promise.all([
          getAccessContext(),
          axios.get(`${process.env.VUE_APP_API_ORIGIN}/project/${this.projectId}`)
        ])
        this.accessContext = context
        this.project = response.data || null
        if (!this.project) this.projectError = 'プロジェクトが見つかりません。'
      } catch (error) {
        this.projectError = 'プロジェクト更新情報を取得できませんでした。'
        console.error('プロジェクト更新取得失敗:', safeErrorDigest(error))
      } finally {
        this.loadingProject = false
      }
    },
    restoreLegacyRouteValue () {
      const route = this.$route || {}
      const params = route.params || {}
      params.value = this.project
    }
  },
  render (createElement) {
    if (this.loadingProject) {
      return createElement('div', { class: 'project-update-loading' }, [
        createElement('span', { class: 'spinner-border spinner-border-sm mr-2', attrs: { role: 'status', 'aria-hidden': 'true' } }),
        createElement('span', 'プロジェクト更新情報を読み込んでいます。')
      ])
    }

    if (this.projectError || !this.project || !this.accessContext) {
      return createElement('div', { class: 'alert alert-danger', attrs: { role: 'alert' } }, [
        this.projectError || 'プロジェクト更新情報を表示できません。'
      ])
    }

    this.restoreLegacyRouteValue()
    const children = [
      createElement(LegacyProjectUpdate, {
        key: `project-update-form-${this.projectId}`
      })
    ]

    if (this.canManageProjectAdministration) {
      children.push(
        createElement(ProjectLeaveSlackNotificationSettings, {
          key: `project-leave-notification-${this.projectId}`,
          props: { projectId: this.projectId },
          on: { updated: () => { this.channelVersion += 1 } }
        }),
        createElement(ProjectPeriodicSlackNotificationSettings, {
          key: `project-periodic-notification-${this.projectId}`,
          props: {
            projectId: this.projectId,
            channelVersion: this.channelVersion
          }
        })
      )
    }

    return createElement('div', { class: 'project-update-page' }, children)
  }
}
